<!-- ========================================== -->
<!-- 1. TOAST ESTÁNDAR (Headless con Borde)     -->
<!-- ========================================== -->
<p-toast [position]="position()">
	<ng-template #headless let-message let-closeFn="closeFn">
		@let isSuccess = message.severity === 'success';
		@let isError = message.severity === 'error';
		@let isWarn = message.severity === 'warn';

		<section
			class="flex items-center w-[22rem] sm:w-[26rem] max-w-[calc(100vw-3rem)] bg-white rounded-2xl shadow-[0_14px_38px_-6px_rgba(15,79,148,0.14),0_4px_16px_-2px_rgba(0,0,0,0.06)] border border-gray-100 p-4 gap-3.5 transition-all duration-300 font-sans group hover:shadow-[0_18px_44px_-6px_rgba(15,79,148,0.2)] overflow-hidden border-l-[6px]"
			[class.border-l-emerald-500]="isSuccess"
			[class.border-l-rose-500]="isError"
			[class.border-l-amber-500]="isWarn"
			[class.border-l-sc-primary]="!isSuccess && !isError && !isWarn"
			role="alert"
			aria-live="polite"
		>
			<!-- Icon Badge según severidad -->
			@if (isSuccess) {
				<div
					class="w-10 h-10 rounded-xl bg-emerald-50 border border-emerald-200/70 flex items-center justify-center shrink-0 text-emerald-600 shadow-xs"
				>
					<i class="ri-checkbox-circle-fill text-xl" aria-hidden="true"></i>
				</div>
			} @else if (isError) {
				<div
					class="w-10 h-10 rounded-xl bg-rose-50 border border-rose-200/70 flex items-center justify-center shrink-0 text-rose-600 shadow-xs"
				>
					<i class="ri-error-warning-fill text-xl" aria-hidden="true"></i>
				</div>
			} @else if (isWarn) {
				<div
					class="w-10 h-10 rounded-xl bg-amber-50 border border-amber-200/70 flex items-center justify-center shrink-0 text-amber-600 shadow-xs"
				>
					<i class="ri-alert-fill text-xl" aria-hidden="true"></i>
				</div>
			} @else {
				<div
					class="w-10 h-10 rounded-xl bg-sky-50 border border-sky-200/70 flex items-center justify-center shrink-0 text-sc-primary shadow-xs"
				>
					<i class="ri-information-fill text-xl" aria-hidden="true"></i>
				</div>
			}

			<!-- Contenido de Texto con centrado vertical dinámico -->
			<div class="flex flex-col flex-1 justify-center min-w-0">
				@if (message.summary) {
					<h3
						class="text-sc-standout-900 font-semibold text-sm leading-snug tracking-tight truncate m-0"
					>
						{{ message.summary }}
					</h3>
				}
				@if (message.detail) {
					<p
						class="text-sc-gray-700 text-xs leading-relaxed break-words font-normal m-0"
						[class.mt-0.5]="message.summary"
					>
						{{ message.detail }}
					</p>
				}
			</div>

			<!-- Botón de Cerrar Accesible -->
			<button
				type="button"
				(click)="closeFn($event)"
				class="w-7 h-7 rounded-lg text-sc-gray-400 hover:text-sc-gray-800 hover:bg-sc-gray-100 transition-colors flex items-center justify-center cursor-pointer border-none bg-transparent shrink-0"
				aria-label="Cerrar notificación"
			>
				<i class="ri-close-line text-lg" aria-hidden="true"></i>
			</button>
		</section>
	</ng-template>
</p-toast>

<!-- ========================================== -->
<!-- 2. PROGRESS / LOADING TOAST (Task Runner)  -->
<!-- ========================================== -->
<p-toast key="progressToast" position="center" [baseZIndex]="9999" [preventOpenDuplicates]="true">
	<ng-template #headless let-message let-closeFn="closeFn">
		<section
			class="flex flex-col w-[22rem] sm:w-[26rem] max-w-[calc(100vw-3rem)] bg-white rounded-2xl shadow-[0_25px_50px_-12px_rgba(15,79,148,0.25)] border border-gray-100 overflow-hidden font-sans border-l-[6px] border-l-sc-primary"
			role="status"
			aria-live="assertive"
		>
			<div class="flex items-center p-5 gap-4">
				<!-- Badge de carga con spinner 100% centrado -->
				<div
					class="w-10 h-10 rounded-xl bg-sky-50 border border-sky-200/80 flex items-center justify-center shrink-0 shadow-xs"
				>
					<span class="sc-toast-spinner" aria-hidden="true"></span>
				</div>

				<!-- Textos de estado con centrado vertical dinámico -->
				<div class="flex flex-col flex-1 justify-center min-w-0">
					@if (message.summary) {
						<h3 class="text-sc-standout-900 font-semibold text-sm tracking-tight m-0 truncate">
							{{ message.summary }}
						</h3>
					}
					@if (message.detail) {
						<p
							class="text-sc-gray-600 text-xs font-normal break-words m-0"
							[class.mt-0.5]="message.summary"
						>
							{{ message.detail }}
						</p>
					}
				</div>
			</div>

			<!-- Barra de progreso integrada -->
			<div class="w-full bg-sc-secondary-1 h-1.5 overflow-hidden">
				<p-progressbar
					mode="indeterminate"
					[style]="{ height: '6px' }"
					styleClass="bg-sc-secondary-1"
					[showValue]="false"
				/>
			</div>

			@if (message.data?.blockUI) {
				<p-blockui
					[blocked]="message.data.blockUI"
					styleClass="bg-sc-standout-900/20 backdrop-blur-sm"
				/>
			}
		</section>
	</ng-template>
</p-toast>

<!-- ========================================== -->
<!-- 3. DETAIL / ERROR HUB TOAST                -->
<!-- ========================================== -->
<p-toast key="detailToast" [position]="position()">
	<ng-template #headless let-message let-closeFn="closeFn">
		<section
			class="flex flex-col w-[24rem] sm:w-[28rem] max-w-[calc(100vw-3rem)] bg-white rounded-2xl shadow-[0_20px_45px_-10px_rgba(0,0,0,0.15)] border border-gray-100 overflow-hidden font-sans border-l-[6px] border-l-amber-500"
			role="alert"
			aria-live="assertive"
		>
			<!-- Header con Banner de Alerta -->
			<div class="flex items-start p-5 gap-3.5 bg-gradient-to-b from-amber-50/40 to-transparent">
				<div
					class="w-10 h-10 rounded-xl bg-amber-100/70 border border-amber-200 flex items-center justify-center shrink-0 text-amber-700 shadow-xs"
				>
					<i class="ri-alert-fill text-xl" aria-hidden="true"></i>
				</div>

				<div class="flex flex-col flex-1 justify-center min-w-0">
					<div class="flex items-center gap-2">
						@if (message.summary) {
							<h3 class="text-sc-standout-900 font-semibold text-sm tracking-tight m-0 truncate">
								{{ message.summary }}
							</h3>
						}
						@if (message.data?.items?.length) {
							<span
								class="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-800 border border-amber-200/60 shrink-0"
							>
								{{ message.data.items.length }} {{ message.data.items.length === 1 ? 'item' : 'items' }}
							</span>
						}
					</div>

					@if (message.detail) {
						<p
							class="text-sc-gray-700 text-xs leading-relaxed font-normal break-words m-0"
							[class.mt-1]="message.summary"
						>
							{{ message.detail }}
						</p>
					}

					<!-- Lista de Detalles / Items con contenedor estilizado -->
					@if (message.data?.items?.length) {
						<div
							class="mt-2.5 max-h-44 overflow-y-auto no-scrollbar bg-sc-gray-50/80 rounded-xl p-3 border border-sc-gray-100"
						>
							<ul class="space-y-1.5 m-0 p-0 list-none text-xs text-sc-standout-900">
								@for (item of message.data.items; track item) {
									<li class="flex items-start gap-2">
										<i
											class="ri-arrow-right-s-fill text-amber-600 mt-0.5 text-xs shrink-0"
											aria-hidden="true"
										></i>
										<span class="leading-relaxed">{{ item }}</span>
									</li>
								}
							</ul>
						</div>
					}
				</div>

				<!-- Botón cerrar -->
				<button
					type="button"
					(click)="closeFn($event)"
					class="w-7 h-7 rounded-lg text-sc-gray-400 hover:text-sc-gray-800 hover:bg-amber-100/50 transition-colors flex items-center justify-center cursor-pointer border-none bg-transparent shrink-0"
					aria-label="Cerrar detalles"
				>
					<i class="ri-close-line text-lg" aria-hidden="true"></i>
				</button>
			</div>
		</section>
	</ng-template>
</p-toast>
